/**
 * Smoothing utilities for telemetry data processing
 *
 * Provides reusable smoothing algorithms for GPS coordinates and numeric values.
 * Used by multiple processors to reduce noise in telemetry data.
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'

/**
 * Remove speed spikes caused by GPS noise.
 *
 * Detects individual readings where speed deviates significantly from the
 * local median and replaces them with linearly interpolated values.
 * This prevents unrealistic acceleration/G-force values downstream.
 *
 * Uses a sliding window median comparison — a spike is a point whose speed
 * differs from the local median by more than a threshold (absolute km/h or
 * relative percentage, whichever is larger). The threshold is generous enough
 * to preserve real braking/acceleration events while catching GPS glitches.
 *
 * @param speeds - Array of speed values (km/h)
 * @param windowSize - Number of neighboring points to consider (default: 5)
 * @param maxDeviationKmh - Absolute deviation threshold in km/h (default: 30)
 * @param maxDeviationFraction - Relative deviation threshold as fraction (default: 0.25)
 * @returns New array with spikes replaced by interpolated values
 */
export function removeSpeedSpikes(
  speeds: number[],
  windowSize: number = 5,
  maxDeviationKmh: number = 30,
  maxDeviationFraction: number = 0.25
): number[] {
  if (speeds.length < windowSize) return [...speeds]

  const result = [...speeds]
  const halfWindow = Math.floor(windowSize / 2)

  // First pass: identify spikes by comparing each point to its local median
  const spikeIndices: number[] = []

  for (let i = 0; i < speeds.length; i++) {
    const start = Math.max(0, i - halfWindow)
    const end = Math.min(speeds.length, i + halfWindow + 1)

    // Get local window (excluding the point itself to avoid self-influence)
    const neighbors: number[] = []
    for (let j = start; j < end; j++) {
      if (j !== i) neighbors.push(speeds[j])
    }

    if (neighbors.length < 2) continue

    neighbors.sort((a, b) => a - b)
    const median = neighbors[Math.floor(neighbors.length / 2)]

    const deviation = Math.abs(speeds[i] - median)
    // Dynamic threshold: max of absolute and relative deviation
    const threshold = Math.max(maxDeviationKmh, median * maxDeviationFraction)

    if (deviation > threshold) {
      spikeIndices.push(i)
    }
  }

  // Second pass: replace spikes with linearly interpolated values
  for (const idx of spikeIndices) {
    // Find nearest non-spike neighbors on each side
    let left = idx - 1
    while (left >= 0 && spikeIndices.includes(left)) left--

    let right = idx + 1
    while (right < speeds.length && spikeIndices.includes(right)) right++

    if (left >= 0 && right < speeds.length) {
      // Interpolate between neighbors
      const fraction = (idx - left) / (right - left)
      result[idx] = speeds[left] + fraction * (speeds[right] - speeds[left])
    } else if (left >= 0) {
      result[idx] = speeds[left]
    } else if (right < speeds.length) {
      result[idx] = speeds[right]
    }
    // else: isolated point, leave as-is
  }

  return result
}

/**
 * Simple moving average smoothing for numeric arrays
 *
 * @param values - Array of values to smooth
 * @param windowSize - Size of the smoothing window (must be >= 2)
 * @returns Smoothed values array
 */
export function smoothValues(values: number[], windowSize: number): number[] {
  if (values.length === 0 || windowSize < 2) return values

  const smoothed: number[] = []
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - halfWindow)
    const end = Math.min(values.length, i + halfWindow + 1)

    let sum = 0
    for (let j = start; j < end; j++) {
      sum += values[j]
    }

    smoothed.push(sum / (end - start))
  }

  return smoothed
}

/**
 * Multi-pass moving average smoothing for noisy data
 *
 * GPS-derived values (acceleration, etc.) are inherently noisy and need
 * aggressive smoothing. Multiple passes provide stronger smoothing while
 * maintaining signal shape better than a single large window.
 *
 * @param values - Array of values to smooth
 * @param windowSize - Size of the smoothing window per pass
 * @param passes - Number of smoothing passes (default: 1)
 * @returns Smoothed values array
 */
export function smoothWithPasses(
  values: number[],
  windowSize: number,
  passes: number = 1
): number[] {
  let smoothed = [...values]

  for (let pass = 0; pass < passes; pass++) {
    const temp: number[] = []
    const halfWindow = Math.floor(windowSize / 2)

    for (let i = 0; i < smoothed.length; i++) {
      const start = Math.max(0, i - halfWindow)
      const end = Math.min(smoothed.length, i + halfWindow + 1)

      let sum = 0
      let count = 0

      for (let j = start; j < end; j++) {
        sum += smoothed[j]
        count++
      }

      temp.push(count > 0 ? sum / count : 0)
    }

    smoothed = temp
  }

  return smoothed
}

/**
 * Gaussian-weighted smoothing for numeric arrays
 *
 * Uses Gaussian weights so points closer to the center contribute more.
 * This reduces noise while preserving peaks better than uniform averaging.
 * Multiple passes approximate a wider Gaussian for stronger smoothing.
 *
 * Particularly effective for acceleration data where numerical differentiation
 * amplifies noise — Gaussian weighting avoids the ripple artifacts that
 * uniform (box) filters create.
 *
 * @param values - Array of values to smooth
 * @param windowSize - Size of the smoothing window
 * @param passes - Number of smoothing passes (default: 1)
 * @returns Smoothed values array
 */
export function smoothValuesGaussian(
  values: number[],
  windowSize: number,
  passes: number = 1
): number[] {
  if (values.length === 0 || windowSize < 2) return values

  let smoothed = [...values]
  const halfWindow = Math.floor(windowSize / 2)
  const sigma = windowSize / 4

  // Pre-compute Gaussian weights for the window
  const weights: number[] = []
  for (let d = 0; d <= halfWindow; d++) {
    weights.push(Math.exp(-(d * d) / (2 * sigma * sigma)))
  }

  for (let pass = 0; pass < passes; pass++) {
    const temp: number[] = []

    for (let i = 0; i < smoothed.length; i++) {
      const windowStart = Math.max(0, i - halfWindow)
      const windowEnd = Math.min(smoothed.length, i + halfWindow + 1)

      let sum = 0
      let weightSum = 0

      for (let j = windowStart; j < windowEnd; j++) {
        const w = weights[Math.abs(j - i)]
        sum += smoothed[j] * w
        weightSum += w
      }

      temp.push(weightSum > 0 ? sum / weightSum : 0)
    }

    smoothed = temp
  }

  return smoothed
}

/**
 * Gaussian-weighted coordinate smoothing for GPS points
 *
 * Uses Gaussian weights to give more importance to points closer to the center.
 * This reduces GPS noise while preserving trajectory shape better than uniform
 * averaging. Essential for accurate turn radius and lean angle calculations.
 *
 * @param points - GPS points to smooth
 * @param windowSize - Size of the smoothing window
 * @returns Smoothed GPS points (only lat/lon modified, other fields preserved)
 */
export function smoothGpsCoordinates(
  points: GpsPoint[],
  windowSize: number
): GpsPoint[] {
  if (points.length < windowSize) return points

  const smoothed: GpsPoint[] = []
  const halfWindow = Math.floor(windowSize / 2)

  for (let i = 0; i < points.length; i++) {
    const windowStart = Math.max(0, i - halfWindow)
    const windowEnd = Math.min(points.length, i + halfWindow + 1)

    let latSum = 0
    let lonSum = 0
    let weightSum = 0

    for (let j = windowStart; j < windowEnd; j++) {
      const distance = Math.abs(j - i)
      // Gaussian weight: closer points get more weight
      const weight = Math.exp(
        -(distance * distance) / (2 * (windowSize / 4) ** 2)
      )
      latSum += points[j].latitude * weight
      lonSum += points[j].longitude * weight
      weightSum += weight
    }

    smoothed.push({
      ...points[i],
      latitude: latSum / weightSum,
      longitude: lonSum / weightSum,
    })
  }

  return smoothed
}
